import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { initIdleLogout } from "@/lib/idle";
import Login from "@/pages/Login";
import SignUp from "@/pages/SignUp";
import TeamChat from "@/pages/TeamChat";
import { ReminderPopup } from "@/components/ReminderPopup";
import PromoterHome from "@/pages/PromoterHome";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminBilling from "@/pages/AdminBilling";
import AdminIssues from "@/pages/AdminIssues";
import AdminUsers from "@/pages/AdminUsers";
import AdminStores from "@/pages/AdminStores";
import AdminZones from "@/pages/AdminZones";
import AdminProcurement from "@/pages/AdminProcurement";
import AdminExpenses from "@/pages/AdminExpenses";
import AdminAudit from "@/pages/AdminAudit";
import AdminBackup from "@/pages/AdminBackup";
import AdminStock from "@/pages/AdminStock";
import AdminRestock from "@/pages/AdminRestock";
import AdminClientNotes from "@/pages/AdminClientNotes";
import AdminNotes from "@/pages/AdminNotes";
import AdminLateEntries from "@/pages/AdminLateEntries";
import AdminDailyEntries from "@/pages/AdminDailyEntries";
import AdminProjects from "@/pages/AdminProjects";
import AdminApprovals from "@/pages/AdminApprovals";
import AdminSalary from "@/pages/AdminSalary";
import SuperAdminPortfolio from "@/pages/SuperAdminPortfolio";
import SupervisorHome from "@/pages/SupervisorHome";
import StoreDetail from "@/pages/StoreDetail";
import ClientDashboard from "@/pages/ClientDashboard";
import ClientReports from "@/pages/ClientReports";
import { getUser, homeForRole } from "@/lib/auth";

// Supervisor-equivalent role bundle (F12): Supervisor, IT, Project Lead all share the same access.
const SUPERVISOR_ROLES = ["Supervisor", "IT", "Project Lead"];
const ADMIN_LIKE = ["Admin", ...SUPERVISOR_ROLES];
const COCA_COLA_ROLES = ["Coca-Cola", "Client", "Admin"]; // Client kept for legacy tokens.

const Protected = ({ children, allow }) => {
  const u = getUser();
  if (!u) return <Navigate to="/login" replace />;
  // Treat legacy "Client" as "Coca-Cola" for permission checks.
  const effectiveRole = u.role === "Client" ? "Coca-Cola" : u.role;
  if (allow && !allow.includes(u.role) && !allow.includes(effectiveRole)) {
    return <Navigate to={homeForRole(u.role)} replace />;
  }
  return children;
};

const RootRedirect = () => {
  const u = getUser();
  return <Navigate to={u ? homeForRole(u.role) : "/login"} replace />;
};

function App() {
  useEffect(() => { initIdleLogout(); }, []);
  return (
    <div className="App">
      <Toaster position="top-center" richColors />
      <BrowserRouter>
        <ReminderPopup />
        <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<SignUp />} />
          <Route path="/promoter" element={<Protected allow={["Promoter"]}><PromoterHome /></Protected>} />
          <Route path="/admin" element={<Protected allow={ADMIN_LIKE}><AdminDashboard /></Protected>} />
          <Route path="/admin/billing" element={<Protected allow={["Admin"]}><AdminBilling /></Protected>} />
          <Route path="/admin/issues" element={<Protected allow={ADMIN_LIKE}><AdminIssues /></Protected>} />
          <Route path="/admin/users" element={<Protected allow={["Admin"]}><AdminUsers /></Protected>} />
          <Route path="/admin/approvals" element={<Protected allow={["Admin", "SuperAdmin", "Project Lead"]}><AdminApprovals /></Protected>} />
          <Route path="/admin/salary" element={<Protected allow={["Admin", "SuperAdmin"]}><AdminSalary /></Protected>} />
          <Route path="/admin/stores" element={<Protected allow={["Admin"]}><AdminStores /></Protected>} />
          <Route path="/admin/zones" element={<Protected allow={["Admin"]}><AdminZones /></Protected>} />
          <Route path="/admin/procurement" element={<Protected allow={ADMIN_LIKE}><AdminProcurement /></Protected>} />
          <Route path="/admin/expenses" element={<Protected allow={ADMIN_LIKE}><AdminExpenses /></Protected>} />
          <Route path="/admin/stock" element={<Protected allow={ADMIN_LIKE}><AdminStock /></Protected>} />
          <Route path="/admin/restock" element={<Protected allow={ADMIN_LIKE}><AdminRestock /></Protected>} />
          <Route path="/admin/audit" element={<Protected allow={["Admin"]}><AdminAudit /></Protected>} />
          <Route path="/admin/backup" element={<Protected allow={["Admin", "SuperAdmin"]}><AdminBackup /></Protected>} />
          <Route path="/admin/late-entries" element={<Protected allow={ADMIN_LIKE}><AdminLateEntries /></Protected>} />
          <Route path="/admin/daily-entries" element={<Protected allow={ADMIN_LIKE}><AdminDailyEntries /></Protected>} />
          <Route path="/admin/client-notes" element={<Protected allow={["Admin"]}><AdminClientNotes /></Protected>} />
          <Route path="/admin/notes" element={<Protected allow={["Admin"]}><AdminNotes /></Protected>} />
          <Route path="/admin/projects" element={<Protected allow={["Admin", "SuperAdmin"]}><AdminProjects /></Protected>} />
          <Route path="/portfolio" element={<Protected allow={["SuperAdmin"]}><SuperAdminPortfolio /></Protected>} />
          <Route path="/supervisor" element={<Protected allow={[...SUPERVISOR_ROLES, "Admin"]}><SupervisorHome /></Protected>} />
          {/* ITEM D.17 (2026-02-29) + re-audit (Item 17 rebuild) — Coordinator/Manager URL rewrite is now COMPLETE.
              Every sidebar destination for Supervisor/Project Lead resolves under /coordinator/* or /manager/*.
              Components are the same (SupervisorHome/AdminDashboard/AdminStock/etc.); only the URL path
              rendered in the browser changes. Backend role strings still "Supervisor" / "Project Lead". */}
          <Route path="/coordinator" element={<Protected allow={[...SUPERVISOR_ROLES, "Admin"]}><SupervisorHome /></Protected>} />
          <Route path="/coordinator/dashboard" element={<Protected allow={[...SUPERVISOR_ROLES, "Admin"]}><AdminDashboard /></Protected>} />
          <Route path="/coordinator/stock" element={<Protected allow={[...SUPERVISOR_ROLES, "Admin"]}><AdminStock /></Protected>} />
          <Route path="/coordinator/restock" element={<Protected allow={[...SUPERVISOR_ROLES, "Admin"]}><AdminRestock /></Protected>} />
          <Route path="/coordinator/procurement" element={<Protected allow={[...SUPERVISOR_ROLES, "Admin"]}><AdminProcurement /></Protected>} />
          <Route path="/coordinator/expenses" element={<Protected allow={[...SUPERVISOR_ROLES, "Admin"]}><AdminExpenses /></Protected>} />
          <Route path="/coordinator/issues" element={<Protected allow={[...SUPERVISOR_ROLES, "Admin"]}><AdminIssues /></Protected>} />
          <Route path="/coordinator/daily-entries" element={<Protected allow={[...SUPERVISOR_ROLES, "Admin"]}><AdminDailyEntries /></Protected>} />
          <Route path="/coordinator/late-entries" element={<Protected allow={[...SUPERVISOR_ROLES, "Admin"]}><AdminLateEntries /></Protected>} />
          <Route path="/manager" element={<Protected allow={[...SUPERVISOR_ROLES, "Admin"]}><SupervisorHome /></Protected>} />
          <Route path="/manager/dashboard" element={<Protected allow={[...SUPERVISOR_ROLES, "Admin"]}><AdminDashboard /></Protected>} />
          <Route path="/manager/stock" element={<Protected allow={[...SUPERVISOR_ROLES, "Admin"]}><AdminStock /></Protected>} />
          <Route path="/manager/restock" element={<Protected allow={[...SUPERVISOR_ROLES, "Admin"]}><AdminRestock /></Protected>} />
          <Route path="/manager/procurement" element={<Protected allow={[...SUPERVISOR_ROLES, "Admin"]}><AdminProcurement /></Protected>} />
          <Route path="/manager/expenses" element={<Protected allow={[...SUPERVISOR_ROLES, "Admin"]}><AdminExpenses /></Protected>} />
          <Route path="/manager/issues" element={<Protected allow={[...SUPERVISOR_ROLES, "Admin"]}><AdminIssues /></Protected>} />
          <Route path="/manager/daily-entries" element={<Protected allow={[...SUPERVISOR_ROLES, "Admin"]}><AdminDailyEntries /></Protected>} />
          <Route path="/manager/late-entries" element={<Protected allow={[...SUPERVISOR_ROLES, "Admin"]}><AdminLateEntries /></Protected>} />
          <Route path="/manager/approvals" element={<Protected allow={["Admin", "SuperAdmin", "Project Lead"]}><AdminApprovals /></Protected>} />
          <Route path="/stores/:storeId" element={<Protected allow={[...ADMIN_LIKE, "Coca-Cola", "Client"]}><StoreDetail /></Protected>} />

          {/* Team Chat — accessible to every logged-in role. */}
          <Route path="/chat" element={<Protected allow={["Admin", "SuperAdmin", "Supervisor", "IT", "Project Lead", "Promoter", "Coca-Cola", "Client"]}><TeamChat /></Protected>} />

          {/* New Coca-Cola routes (F12) */}
          <Route path="/coca-cola" element={<Protected allow={COCA_COLA_ROLES}><ClientDashboard /></Protected>} />
          <Route path="/coca-cola/reports" element={<Protected allow={COCA_COLA_ROLES}><ClientReports /></Protected>} />

          {/* Legacy /client routes — redirect to /coca-cola so old bookmarks / cached PWA links keep working */}
          <Route path="/client" element={<Navigate to="/coca-cola" replace />} />
          <Route path="/client/reports" element={<Navigate to="/coca-cola/reports" replace />} />
          <Route path="/client/*" element={<Navigate to="/coca-cola" replace />} />

          <Route path="*" element={<RootRedirect />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
